import { type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ArrowUpDown, MoreHorizontal } from 'lucide-react'
import { format } from 'date-fns'
import type { Run } from '@/api/types'

type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning'

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'destructive'
    case 'processing':
      return 'warning'
    case 'queued':
      return 'secondary'
    default:
      return 'default'
  }
}

interface ColumnActions {
  onView?: (run: Run) => void
  onRetry?: (run: Run) => void
  onDelete?: (run: Run) => void
}

export function createColumns(actions?: ColumnActions): ColumnDef<Run>[] {
  return [
    // Selection column
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    // ID column
    {
      accessorKey: 'fileId',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4"
        >
          ID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-mono text-xs truncate max-w-[100px]">
          {row.getValue('fileId')}
        </div>
      ),
    },
    // Filename column
    {
      accessorKey: 'fileName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4"
        >
          Filename
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const fileName = row.getValue('fileName') as string
        const originalName = row.original.originalName
        return (
          <div className="font-medium truncate max-w-[200px]">
            {fileName || originalName || 'Unknown'}
          </div>
        )
      },
    },
    // Workflow column
    {
      accessorKey: 'workflow',
      header: 'Workflow',
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue('workflow')}</div>
      ),
    },
    // Status column
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string
        return <Badge variant={getStatusVariant(status)}>{status}</Badge>
      },
    },
    // Phenotype column
    {
      accessorKey: 'phenotype',
      header: 'Phenotype',
      cell: ({ row }) => {
        const phenotype = row.getValue('phenotype') as string | undefined
        return phenotype ? (
          <div className="truncate max-w-[120px]">{phenotype}</div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },
    // Created column
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4"
        >
          Created
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string
        return (
          <div className="text-muted-foreground">
            {format(new Date(date), 'MMM dd, yyyy HH:mm')}
          </div>
        )
      },
    },
    // Actions column
    {
      id: 'actions',
      cell: ({ row }) => {
        const run = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions?.onView && (
                <DropdownMenuItem onClick={() => actions.onView?.(run)}>
                  View Details
                </DropdownMenuItem>
              )}
              {actions?.onRetry && run.status === 'failed' && (
                <DropdownMenuItem onClick={() => actions.onRetry?.(run)}>
                  Retry
                </DropdownMenuItem>
              )}
              {actions?.onDelete && (
                <DropdownMenuItem
                  onClick={() => actions.onDelete?.(run)}
                  className="text-destructive"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}
